import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { parsearPlanilhaSemTravar } from "../utils/parsePlanilhaAsync";
import { D } from "../utils/distribuicaoFields";
import { F } from "../utils/gerencialFields";
import { useToast } from "./ToastContext";
import { useAuth } from "./AuthContext";
import * as planilhaLegadoApi from "../services/planilhaLegado";
import { MODULO_PLANILHA_LEGADO } from "../config/modulos";
import { carimbarOrdem } from "../utils/fluxoExecutivo";
import { aoLimparDadosDoTenant, chaveTenant, getItemTenant, removeItemTenant, setItemTenant } from "../utils/tenantStorage";

const STORAGE_KEY = "pharuerp:planilha-selecionada";
const HANDLE_DB_NAME = "dash-pcp-planilha";
const HANDLE_STORE_NAME = "handles";
const HANDLE_STORAGE_KEY = "planilha";
const BLOB_DB_NAME = "dash-pcp-planilha-blob";
const BLOB_STORE_NAME = "blobs";
const BLOB_STORAGE_KEY = "planilha";

// Segunda fonte de dados: planilha DISTRIBUICAO DE PROJETOS <ano>.xlsm.
// Storage paralelo e isolado — chaves nao colidem com a planilha do GERENCIAL.
const DIST_STORAGE_KEY = "pharuerp:planilha-distribuicao-selecionada";
const DIST_BLOB_DB_NAME = "dash-pcp-planilha-dist-blob";
const DIST_BLOB_STORE_NAME = "blobs";
const DIST_BLOB_STORAGE_KEY = "planilha-dist";
// J-087: 5s era demasiado para arquivos .xlsm de 11MB que levam ~1-2s
// para reparsear no cliente. Subimos para 30s — equilibrio entre frescor
// dos dados e responsividade da UI em maquinas fracas.
const AUTO_RELOAD_INTERVAL_MS = 30000;
const PLANILHA_API_URL = process.env.REACT_APP_PLANILHA_API_URL || "";
const API_BASE_URL =
  PLANILHA_API_URL || (process.env.NODE_ENV === "production" ? "" : "http://localhost:3001");
const HAS_PLANILHA_API = Boolean(API_BASE_URL);
const DadosContext = createContext();

// Fonte "servidor" (2026-09-22): o backend le a planilha no Google Drive e a
// entrega autenticada por unidade — e o que faz o celular/tablet abrir as
// telas do Legado sem escolher arquivo. Sem configuracao, sem permissao ou com
// o servidor fora, vale o fluxo antigo (arquivo/copia local).
export const FONTE_SERVIDOR = "servidor";
export const FONTE_ARQUIVO = "arquivo";
const PERMISSAO_PLANILHA_SERVIDOR = "planilha_legado:view";
const VERIFICACAO_SERVIDOR_MS = 30000;
// Download/parse que falhou nao e repetido para o MESMO md5 antes desta
// espera: sem ela a verificacao de 30 s rebaixava 11 MB sem parar (e
// repetia o toast de erro do parse). "Atualizar dados" forca na hora.
const ESPERA_APOS_FALHA_MS = 5 * 60 * 1000;
const TIPO_XLSM = "application/vnd.ms-excel.sheet.macroEnabled.12";
// md5 da copia do servidor guardada no IndexedDB. Ausente = o blob salvo NAO
// e a copia do servidor (ou nao ha blob). Prefixada pelo tenant (MT-4.7).
const MD5_SERVIDOR_STORAGE_KEY = "pharuerp:planilha-servidor-md5";

// Token compartilhado do servidor local (H-070/H-071): obtido sob demanda via
// GET /api/token e enviado no header X-PharuERP-Token nos endpoints de escrita
// (POST /api/planilha, /api/planilha/upload). Sem isso, o upload via navegador
// respondia 401 "Token invalido.". Cache em modulo; so guarda valor valido,
// permitindo nova tentativa se a primeira falhar.
let _planilhaTokenCache = null;
async function getPlanilhaToken() {
  if (!HAS_PLANILHA_API) return "";
  if (_planilhaTokenCache) return _planilhaTokenCache;
  try {
    const r = await fetch(`${API_BASE_URL}/api/token`);
    if (r.ok) {
      const j = await r.json();
      _planilhaTokenCache = (j && j.token) || "";
    }
  } catch {
    /* mantem null para retentar na proxima chamada */
  }
  return _planilhaTokenCache || "";
}

async function fetchJson(endpoint, options) {
  if (!HAS_PLANILHA_API) {
    const error = new Error("Selecione a planilha pelo navegador para carregar os dados.");
    error.code = "NO_PLANILHA_API";
    throw error;
  }

  const opts = { ...(options || {}) };

  // Endpoints de escrita exigem o token local — injeta o header.
  const metodo = (opts.method || "GET").toUpperCase();
  if (metodo !== "GET" && metodo !== "HEAD") {
    const tk = await getPlanilhaToken();
    if (tk) {
      opts.headers = { ...(opts.headers || {}), "X-PharuERP-Token": tk };
    }
  }

  let controller;
  let timerId;

  const timeoutMs = opts.timeout ?? 60000;
  if (typeof AbortController !== 'undefined' && !opts.signal) {
    controller = new AbortController();
    opts.signal = controller.signal;
    timerId = setTimeout(() => controller.abort(), timeoutMs);
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, opts);

    // F-044: Vercel SPA retorna index.html (HTTP 200) para qualquer
    // /api/* nao roteada — `response.json()` falha silenciosamente.
    // Verificamos Content-Type ANTES de tentar parse para detectar o
    // erro real e gerar mensagem util ao usuario.
    const contentType = response.headers.get("content-type") || "";
    let payload = null;

    if (contentType.includes("application/json")) {
      try {
        payload = await response.json();
      } catch (error) {
        payload = null;
      }
    } else {
      // Resposta nao e JSON — provavelmente fallback do hosting (HTML).
      const erro = new Error(
        `Backend offline ou nao configurado para /api/* (recebido ${contentType || "sem tipo"}).`
      );
      erro.code = "BACKEND_HTML_FALLBACK";
      throw erro;
    }

    if (!response.ok) {
      const erro = new Error(
        payload?.erro || payload?.detalhe || `Falha na requisicao (${response.status})`
      );
      erro.payload = payload;
      throw erro;
    }

    return payload;
  } catch (err) {
    if (err.name === 'AbortError') {
      const erro = new Error(`Tempo esgotado ao contatar o servidor (${timeoutMs}ms)`);
      throw erro;
    }
    throw err;
  } finally {
    if (timerId) clearTimeout(timerId);
  }
}

function buscarDadosPlanilha() {
  return fetchJson("/api/dados");
}

function buscarStatusPlanilha() {
  return fetchJson("/api/status");
}

function abrirSeletorPlanilha() {
  return fetchJson("/api/planilha/selecionar", { method: "POST" });
}

function isNetworkError(error) {
  return (
    error?.code === "NO_PLANILHA_API" ||
    error?.code === "BACKEND_HTML_FALLBACK" ||
    error?.name === "TypeError" ||
    /failed to fetch|networkerror|load failed/i.test(error?.message || "")
  );
}

function statusSemPlanilha() {
  return {
    status: "arquivo_nao_encontrado",
    origem: "navegador",
    pastaPai: null,
    caminhoAtual: null,
    arquivoEncontrado: null,
    caminhoConfigurado: null,
    configuracaoSalva: false,
    erroConfiguracao: null,
    arquivosNaPasta: [],
    timestamp: new Date().toISOString(),
  };
}

function statusFromPayload(payload) {
  return {
    status: "ok",
    origem: payload?.meta?.origemArquivo || "navegador",
    pastaPai: null,
    caminhoAtual: payload?.meta?.caminhoArquivo || null,
    arquivoEncontrado: payload?.meta?.arquivoLido || null,
    caminhoConfigurado: null,
    configuracaoSalva: false,
    erroConfiguracao: null,
    arquivosNaPasta: payload?.meta?.arquivoLido ? [payload.meta.arquivoLido] : [],
    timestamp: payload?.meta?.timestamp || new Date().toISOString(),
  };
}

function lerMd5Servidor() {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(chaveTenant(MD5_SERVIDOR_STORAGE_KEY)) || null;
  } catch {
    return null;
  }
}

function gravarMd5Servidor(md5) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(chaveTenant(MD5_SERVIDOR_STORAGE_KEY), md5);
  } catch {
    // Sem o md5 a proxima abertura baixa de novo — custo de banda, nao de dado.
  }
}

function limparMd5Servidor() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(chaveTenant(MD5_SERVIDOR_STORAGE_KEY));
  } catch {
    // Mesmo efeito do gravar: no pior caso, um download a mais.
  }
}

function paginaOculta() {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

function readStoredPlanilha() {
  if (typeof window === "undefined") return null;

  try {
    const raw = getItemTenant(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function clearStoredPlanilha() {
  if (typeof window === "undefined") return;

  limparMd5Servidor();

  try {
    removeItemTenant(STORAGE_KEY);
  } catch {
    // Ignore storage failures; the in-memory state is still updated.
  }

  clearStoredPlanilhaBlob();
}

function supportsPersistentFilePicker() {
  return typeof window !== "undefined" && typeof window.showOpenFilePicker === "function";
}

function supportsIndexedDb() {
  return typeof window !== "undefined" && Boolean(window.indexedDB);
}

function openHandleDb() {
  if (!supportsIndexedDb()) {
    return Promise.resolve(null);
  }

  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(HANDLE_DB_NAME, 1);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(HANDLE_STORE_NAME)) {
        db.createObjectStore(HANDLE_STORE_NAME);
      }
    };

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

async function withHandleStore(mode, callback) {
  const db = await openHandleDb();
  if (!db) return null;

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(HANDLE_STORE_NAME, mode);
    const store = transaction.objectStore(HANDLE_STORE_NAME);
    const request = callback(store);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    transaction.oncomplete = () => db.close();
    transaction.onerror = () => {
      db.close();
      reject(transaction.error);
    };
  });
}

function readStoredFileHandle() {
  return withHandleStore("readonly", (store) => store.get(chaveTenant(HANDLE_STORAGE_KEY)));
}

function writeStoredFileHandle(handle) {
  return withHandleStore("readwrite", (store) => store.put(handle, chaveTenant(HANDLE_STORAGE_KEY)));
}

function clearStoredFileHandle() {
  return withHandleStore("readwrite", (store) => store.delete(chaveTenant(HANDLE_STORAGE_KEY))).catch(() => null);
}

function openBlobDb() {
  if (!supportsIndexedDb()) {
    return Promise.resolve(null);
  }

  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(BLOB_DB_NAME, 1);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(BLOB_STORE_NAME)) {
        db.createObjectStore(BLOB_STORE_NAME);
      }
    };

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

async function withBlobStore(mode, callback) {
  const db = await openBlobDb();
  if (!db) return null;

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(BLOB_STORE_NAME, mode);
    const store = transaction.objectStore(BLOB_STORE_NAME);
    const request = callback(store);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    transaction.oncomplete = () => db.close();
    transaction.onerror = () => {
      db.close();
      reject(transaction.error);
    };
  });
}

function readStoredPlanilhaBlob() {
  return withBlobStore("readonly", (store) => store.get(chaveTenant(BLOB_STORAGE_KEY)));
}

function writeStoredPlanilhaBlob(blob) {
  return withBlobStore("readwrite", (store) => store.put(blob, chaveTenant(BLOB_STORAGE_KEY)));
}

function clearStoredPlanilhaBlob() {
  return withBlobStore("readwrite", (store) => store.delete(chaveTenant(BLOB_STORAGE_KEY))).catch(() => null);
}

// --- IndexedDB paralelo para a planilha de DISTRIBUICAO ---
function openDistBlobDb() {
  if (!supportsIndexedDb()) {
    return Promise.resolve(null);
  }

  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DIST_BLOB_DB_NAME, 1);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(DIST_BLOB_STORE_NAME)) {
        db.createObjectStore(DIST_BLOB_STORE_NAME);
      }
    };

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

async function withDistBlobStore(mode, callback) {
  const db = await openDistBlobDb();
  if (!db) return null;

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(DIST_BLOB_STORE_NAME, mode);
    const store = transaction.objectStore(DIST_BLOB_STORE_NAME);
    const request = callback(store);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    transaction.oncomplete = () => db.close();
    transaction.onerror = () => {
      db.close();
      reject(transaction.error);
    };
  });
}

function readStoredDistBlob() {
  return withDistBlobStore("readonly", (store) => store.get(chaveTenant(DIST_BLOB_STORAGE_KEY)));
}

function writeStoredDistBlob(blob) {
  return withDistBlobStore("readwrite", (store) => store.put(blob, chaveTenant(DIST_BLOB_STORAGE_KEY)));
}

function clearStoredDistBlob() {
  return withDistBlobStore("readwrite", (store) => store.delete(chaveTenant(DIST_BLOB_STORAGE_KEY))).catch(() => null);
}

function readStoredDistMeta() {
  if (typeof window === "undefined") return null;
  try {
    const raw = getItemTenant(DIST_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Logout: a planilha guardada no aparelho e da empresa de quem saiu. Num
// computador compartilhado, a proxima pessoa (talvez de outra empresa) nao
// pode abrir com ela carregada.
aoLimparDadosDoTenant(() => {
  clearStoredFileHandle();
  clearStoredPlanilhaBlob();
  clearStoredDistBlob();
});

function clearStoredDistMeta() {
  if (typeof window === "undefined") return;
  try {
    removeItemTenant(DIST_STORAGE_KEY);
  } catch {
    // ignora — o estado em memoria ja foi limpo
  }
  clearStoredDistBlob();
}

async function writeStoredDistPlanilha(file) {
  if (typeof window === "undefined") return;
  await writeStoredDistBlob(file);
  setItemTenant(
    DIST_STORAGE_KEY,
    JSON.stringify({
      name: file.name,
      type: file.type || "application/vnd.ms-excel.sheet.macroEnabled.12",
      lastModified: file.lastModified || Date.now(),
      size: file.size,
      savedAt: new Date().toISOString(),
    })
  );
}

async function storedDistToFile(stored) {
  if (!stored?.name) return null;
  let blob = null;
  try {
    blob = await readStoredDistBlob();
  } catch {
    blob = null;
  }
  if (!blob) return null;
  return new File([blob], stored.name, {
    type: stored.type || blob.type,
    lastModified: stored.lastModified || Date.now(),
  });
}

async function hasFileHandlePermission(handle) {
  if (!handle || typeof handle.queryPermission !== "function") {
    return Boolean(handle);
  }

  try {
    return (await handle.queryPermission({ mode: "read" })) === "granted";
  } catch {
    return false;
  }
}

async function requestFileHandlePermission(handle) {
  if (!handle) return false;

  if (await hasFileHandlePermission(handle)) {
    return true;
  }

  if (typeof handle.requestPermission !== "function") {
    return true;
  }

  try {
    return (await handle.requestPermission({ mode: "read" })) === "granted";
  } catch {
    return false;
  }
}

async function getFileFromHandle(handle, { requestPermission = false } = {}) {
  if (!handle) return null;

  const allowed = requestPermission
    ? await requestFileHandlePermission(handle)
    : await hasFileHandlePermission(handle);

  if (!allowed) {
    return null;
  }

  return handle.getFile();
}

function getFileSignature(file) {
  if (!file) return "";
  return `${file.name}:${file.size}:${file.lastModified || 0}`;
}

async function writeStoredPlanilha(file) {
  if (typeof window === "undefined") return;

  await writeStoredPlanilhaBlob(file);

  setItemTenant(
    STORAGE_KEY,
    JSON.stringify({
      name: file.name,
      type: file.type || "application/vnd.ms-excel.sheet.macroEnabled.12",
      lastModified: file.lastModified || Date.now(),
      size: file.size,
      savedAt: new Date().toISOString(),
    })
  );
}

async function storedPlanilhaToFile(stored) {
  if (!stored?.name) return null;

  let blob = null;
  try {
    blob = await readStoredPlanilhaBlob();
  } catch {
    blob = null;
  }

  if (!blob && stored.dataUrl) {
    try {
      const response = await fetch(stored.dataUrl);
      blob = await response.blob();
    } catch {
      blob = null;
    }
  }

  if (!blob) return null;

  return new File([blob], stored.name, {
    type: stored.type || blob.type,
    lastModified: stored.lastModified || Date.now(),
  });
}

/**
 * Data de corte da ordem do executivo (ISO) ou null. Sem resposta vale a
 * ordem antiga — a mesma de quem nunca configurou.
 */
async function lerOrdemExecutivo(request, contratado = true) {
  if (typeof request !== "function" || !contratado) return null;
  try {
    const resposta = await planilhaLegadoApi.ordemExecutivo(request);
    return resposta?.revisao_antes_desde ?? null;
  } catch {
    return null;
  }
}

export function DadosProvider({ children }) {
  // Avisos de carga de planilha (2026-08-04): o parse saiu da thread
  // principal (parsePlanilhaAsync) e o usuario passou a ser AVISADO — antes o
  // ERP congelava em silencio e nao dava para nem pintar um toast.
  const toast = useToast();
  const [dados, setDados] = useState([]);
  const [meta, setMeta] = useState(null);
  const [arquivoPlanilha, setArquivoPlanilha] = useState(null);
  const [arquivoHandle, setArquivoHandle] = useState(null);
  const [arquivoSignature, setArquivoSignature] = useState("");
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [statusPlanilha, setStatusPlanilha] = useState(null);
  const [planilhaErro, setPlanilhaErro] = useState(null);
  const [alterandoPlanilha, setAlterandoPlanilha] = useState(false);

  // Segunda fonte de dados: planilha de DISTRIBUICAO DE PROJETOS.
  // Estado paralelo e isolado — nao interfere com o Gerenciador.
  const [dadosDistribuicao, setDadosDistribuicao] = useState([]);
  const [metaDistribuicao, setMetaDistribuicao] = useState(null);
  const [arquivoDist, setArquivoDist] = useState(null);
  const [statusPlanilhaDist, setStatusPlanilhaDist] = useState(null);
  const [planilhaDistErro, setPlanilhaDistErro] = useState(null);
  const [alterandoPlanilhaDist, setAlterandoPlanilhaDist] = useState(false);

  // ---- Fonte "servidor" (planilha lida do Google Drive pelo backend) ----
  const auth = useAuth() || {};
  const [fonte, setFonte] = useState(FONTE_ARQUIVO);
  // Data da VERSAO da planilha (modificacao no Drive / lastModified do
  // arquivo) — nao a hora em que o navegador leu.
  const [atualizadoEm, setAtualizadoEm] = useState(null);
  const [erroServidor, setErroServidor] = useState(null);
  const [servidorConfigurado, setServidorConfigurado] = useState(false);
  // md5 da planilha EM MEMORIA quando ela e a copia do servidor; null quando
  // os dados vieram de um arquivo escolhido no aparelho.
  const md5ServidorRef = useRef(null);
  // Capacidade da Fabrica: o extrato por cabecalho sai do MESMO parse (worker)
  // e a versao do arquivo (md5 do Drive) identifica a foto enviada ao
  // backend. `versaoArquivo` e null quando a planilha veio do aparelho: so a
  // copia do Drive gera foto, para todos verem a mesma coisa.
  const [extratoCapacidade, setExtratoCapacidade] = useState(null);
  const [versaoArquivo, setVersaoArquivo] = useState(null);
  const sincronizacaoEmCursoRef = useRef(null);
  const avisoServidorMostradoRef = useRef(false);

  // Ordem das etapas do executivo: a partir desta data da unidade, REVISÃO
  // vem antes da APROVAÇÃO (utils/fluxoExecutivo.js). Lida uma vez por
  // sessão e carimbada nas linhas expostas em `dados`, para todas as telas
  // concordarem. O request vai por ref: a identidade dele não deve refazer
  // a leitura.
  const [revisaoAntesDesde, setRevisaoAntesDesde] = useState(null);
  const requestRef = useRef(auth.request);
  requestRef.current = auth.request;
  const autenticado = Boolean(auth.authenticated);
  // Modulo da planilha do Gerenciador (ADR-0102): sem ele, nada daqui vai ao servidor.
  const planilhaContratada = Boolean(auth.temModulo?.(MODULO_PLANILHA_LEGADO));
  // { md5, em } do ultimo download/parse da copia do servidor que falhou.
  const falhaServidorRef = useRef(null);

  async function carregarArquivoLocal(
    file,
    { persist = true, handle = null, origem = FONTE_ARQUIVO, md5Servidor = null } = {},
  ) {
    if (!file) {
      throw new Error("Nenhum arquivo de planilha foi selecionado.");
    }

    setPlanilhaErro(null);
    setArquivoPlanilha(file);
    setArquivoSignature(getFileSignature(file));

    if (origem === FONTE_SERVIDOR) {
      // O handle do computador continua salvo (plano B se o Drive for
      // desligado); so a checagem local de 30 s para, porque agora quem
      // avisa que a planilha mudou e o servidor.
      setArquivoHandle(null);
    } else if (handle) {
      setArquivoHandle(handle);
    } else {
      setArquivoHandle(null);
      await clearStoredFileHandle();
    }

    if (persist) {
      // O blob vai ser sobrescrito por um arquivo que nao e a copia do
      // servidor: o md5 guardado deixaria de descreve-lo.
      limparMd5Servidor();
      try {
        await writeStoredPlanilha(file);
      } catch (error) {
        setPlanilhaErro(
          "Planilha carregada, mas não foi possível salvá-la no armazenamento local. A atualização automática continuará disponível quando o navegador permitir acesso persistente ao arquivo."
        );
      }
    }

    // Aviso imediato: com o parse em worker a UI segue viva, entao o toast
    // realmente aparece (antes o congelamento engolia qualquer sinalizacao).
    toast.info(`Carregando planilha ${file.name}…`, {
      dedupKey: "planilha-legado-carregando",
    });
    let resultado;
    try {
      resultado = await parsearPlanilhaSemTravar("gerencial", file);
    } catch (error) {
      // Copia do servidor: quem avisa e registrarFalhaServidor (uma vez por
      // sessao) — aqui o toast se repetiria a cada nova tentativa.
      if (origem !== FONTE_SERVIDOR) {
        toast.error(
          `Falha ao carregar a planilha ${file.name}: ${error.message}`,
          { dedupKey: "planilha-legado-erro" },
        );
      }
      throw error;
    }
    setDados(resultado.gerencial || []);
    setMeta(resultado.meta || null);
    setExtratoCapacidade(resultado.capacidade || null);
    setErro(null);
    setStatusPlanilha(statusFromPayload(resultado));
    setPlanilhaErro((current) => current || null);
    // Restaurar o blob sem regravar (persist=false) pode estar trazendo a
    // copia do servidor salva antes — o md5 guardado diz se e ela.
    md5ServidorRef.current =
      origem === FONTE_SERVIDOR ? md5Servidor : persist ? null : lerMd5Servidor();
    setFonte(md5ServidorRef.current ? FONTE_SERVIDOR : FONTE_ARQUIVO);
    setVersaoArquivo(md5ServidorRef.current || null);
    setAtualizadoEm(file.lastModified ? new Date(file.lastModified).toISOString() : null);
    toast.success(
      `Planilha do módulo legado carregada: ${resultado.gerencial?.length || 0} ambientes.`,
      { dedupKey: "planilha-legado-ok" },
    );
    return statusFromPayload(resultado);
  }

  // ---------- Fonte "servidor" ----------

  function servidorPermitido() {
    if (typeof auth.request !== "function") return false;
    // Permissoes ainda nao chegaram (primeiro login): pergunta ao servidor
    // mesmo assim — o 403 decide, em silencio.
    if (!auth.permissoes) return true;
    // Sem o modulo contratado (ADR-0102) a rota responde 404: nem pergunta,
    // nem a cada 30 s.
    if (typeof auth.temModulo === "function" && !auth.temModulo(MODULO_PLANILHA_LEGADO)) return false;
    return typeof auth.hasPermission === "function"
      && auth.hasPermission(PERMISSAO_PLANILHA_SERVIDOR);
  }

  // Aviso discreto: o erro fica exposto no contexto (a aba Planilhas mostra)
  // e vira UM toast por sessao — o status roda a cada 30 s e nao pode
  // repetir mensagem.
  function registrarFalhaServidor(mensagem) {
    setErroServidor(mensagem);
    if (avisoServidorMostradoRef.current) return;
    avisoServidorMostradoRef.current = true;
    toast.warning(
      `Não foi possível carregar a planilha do Google Drive: ${mensagem} Veja em Configurações > Planilhas.`,
      { dedupKey: "planilha-legado-servidor" },
    );
  }

  async function guardarCopiaDoServidor(file, md5) {
    limparMd5Servidor();
    try {
      await writeStoredPlanilha(file);
      gravarMd5Servidor(md5);
    } catch {
      setPlanilhaErro(
        "Planilha carregada do Google Drive, mas o navegador não deixou guardar a cópia para abrir sem internet."
      );
    }
  }

  async function restaurarCopiaDoServidor(arquivoStatus) {
    if (lerMd5Servidor() !== arquivoStatus.md5) return false;
    const file = await storedPlanilhaToFile(readStoredPlanilha());
    if (!file) return false;
    try {
      await carregarArquivoLocal(file, {
        persist: false,
        origem: FONTE_SERVIDOR,
        md5Servidor: arquivoStatus.md5,
      });
    } catch {
      // Copia local corrompida: esquecer o md5 faz o chamador baixar de novo
      // (senao ficaria presa tentando restaurar o mesmo blob ruim).
      limparMd5Servidor();
      return false;
    }
    return true;
  }

  function falhouHaPoucoComEsteArquivo(md5) {
    const falha = falhaServidorRef.current;
    return Boolean(falha && falha.md5 === md5 && Date.now() - falha.em < ESPERA_APOS_FALHA_MS);
  }

  async function baixarDoServidor(arquivoStatus) {
    setAlterandoPlanilha(true);
    try {
      const baixado = await planilhaLegadoApi.baixarArquivo();
      // ETag/Content-Disposition podem nao ser legiveis via CORS: o status
      // (lido segundos antes) completa.
      const md5 = baixado.md5 || arquivoStatus.md5;
      const file = new File([baixado.buffer], arquivoStatus.nome || baixado.nome, {
        type: TIPO_XLSM,
        lastModified: Date.parse(arquivoStatus.modificado_em) || Date.now(),
      });
      await carregarArquivoLocal(file, { persist: false, origem: FONTE_SERVIDOR, md5Servidor: md5 });
      await guardarCopiaDoServidor(file, md5);
    } finally {
      setAlterandoPlanilha(false);
    }
  }

  // Devolve true quando os dados na tela sao a copia do servidor (recem-
  // baixada, restaurada ou ja carregada e sem mudanca); false manda o
  // chamador para o fluxo antigo (arquivo / copia local). Nunca rejeita.
  async function executarSincronizacao({ forcar = false } = {}) {
    let st;
    try {
      st = await planilhaLegadoApi.status(auth.request);
    } catch (error) {
      if (error?.status === 401 || error?.status === 403) {
        setServidorConfigurado(false);
        return false;
      }
      registrarFalhaServidor(error?.message || "servidor indisponível.");
      return Boolean(md5ServidorRef.current);
    }

    if (!st?.configurado) {
      setServidorConfigurado(false);
      setErroServidor(null);
      return false;
    }
    setServidorConfigurado(true);

    const arquivoStatus = st.arquivo;
    if (!arquivoStatus?.md5) {
      registrarFalhaServidor(st.erro || "o servidor ainda não conseguiu ler o arquivo no Google Drive.");
      return Boolean(md5ServidorRef.current);
    }

    const precisaTrocar = md5ServidorRef.current !== arquivoStatus.md5;
    if (precisaTrocar && !forcar && falhouHaPoucoComEsteArquivo(arquivoStatus.md5)) {
      return Boolean(md5ServidorRef.current);
    }

    try {
      if (precisaTrocar) {
        const restaurada = await restaurarCopiaDoServidor(arquivoStatus);
        if (!restaurada) await baixarDoServidor(arquivoStatus);
      }
      falhaServidorRef.current = null;
      setFonte(FONTE_SERVIDOR);
      setAtualizadoEm(arquivoStatus.modificado_em || null);
      setErroServidor(st.erro || null);
      return true;
    } catch (error) {
      falhaServidorRef.current = { md5: arquivoStatus.md5, em: Date.now() };
      registrarFalhaServidor(error?.message || "falha ao baixar a planilha.");
      return Boolean(md5ServidorRef.current);
    }
  }

  function sincronizarComServidor({ forcar = false } = {}) {
    if (!servidorPermitido()) return Promise.resolve(false);
    // Boot, "Atualizar dados" e o intervalo podem coincidir: uma sincronizacao
    // por vez, e quem chega no meio espera a mesma.
    if (!sincronizacaoEmCursoRef.current) {
      sincronizacaoEmCursoRef.current = executarSincronizacao({ forcar }).finally(() => {
        sincronizacaoEmCursoRef.current = null;
      });
    }
    return sincronizacaoEmCursoRef.current;
  }

  async function carregarStatus() {
    const status = await buscarStatusPlanilha();
    setStatusPlanilha(status);
    setPlanilhaErro(status?.erroConfiguracao || null);
    return status;
  }

  async function carregarDados() {
    setLoading(true);
    setErro(null);

    try {
      const json = await buscarDadosPlanilha();

      setDados(json.gerencial || []);
      setMeta(json.meta || null);
      setExtratoCapacidade(json.capacidade || null);
      setVersaoArquivo(null);
      setErro(null);
      setPlanilhaErro(null);
      setStatusPlanilha((atual) => ({
        ...(atual || {}),
        status: "ok",
        origem: json?.meta?.origemArquivo || atual?.origem || null,
        caminhoAtual: json?.meta?.caminhoArquivo || atual?.caminhoAtual || null,
        arquivoEncontrado: json?.meta?.arquivoLido || atual?.arquivoEncontrado || null,
        timestamp: json?.meta?.timestamp || atual?.timestamp || null,
      }));

      return json;
    } catch (error) {
      // Para erros de rede (servidor offline / fallback HTML), o caller
      // — `recarregarDados` ou o useEffect inicial — vai tentar restaurar
      // do IndexedDB. NÃO descartamos `dados` nem mostramos "Failed to
      // fetch" piscando na UI: deixamos o estado atual intacto enquanto
      // o fallback acontece. O caller decide se mostra erro depois.
      if (!isNetworkError(error)) {
        setDados([]);
        setMeta(null);
        setExtratoCapacidade(null);
        setVersaoArquivo(null);
        setErro(error.message);

        if (error.payload?.statusPlanilha) {
          setStatusPlanilha(error.payload.statusPlanilha);
          setPlanilhaErro(error.payload.statusPlanilha?.erroConfiguracao || null);
        }
      }

      throw error;
    } finally {
      setLoading(false);
    }
  }

  async function recarregarDados() {
    // Limpa qualquer erro/aviso antigo antes da tentativa.
    setErro(null);
    setPlanilhaErro(null);

    if (await sincronizarComServidor({ forcar: true })) {
      return { fonte: FONTE_SERVIDOR };
    }

    if (!HAS_PLANILHA_API) {
      let storedHandle = arquivoHandle;
      if (!storedHandle) {
        try {
          storedHandle = await readStoredFileHandle();
        } catch (error) {
          storedHandle = null;
        }
      }
      const handleFile = await getFileFromHandle(storedHandle, { requestPermission: true });

      if (handleFile) {
        return carregarArquivoLocal(handleFile, { persist: true, handle: storedHandle });
      }

      if (arquivoPlanilha) {
        return uploadPlanilha(arquivoPlanilha);
      }

      const stored = readStoredPlanilha();
      const storedFile = await storedPlanilhaToFile(stored);
      if (storedFile) {
        return uploadPlanilha(storedFile);
      }

      const message = "Selecione a planilha pelo navegador para carregar os dados.";
      setPlanilhaErro(message);
      throw new Error(message);
    }

    try {
      await carregarStatus();
    } catch (error) {
      // Erros de rede aqui sao tolerados — caem no fallback abaixo.
      if (!isNetworkError(error)) {
        setPlanilhaErro(error.message || null);
      }
    }

    try {
      return await carregarDados();
    } catch (error) {
      // Servidor caiu / nao respondeu: restaura do IndexedDB em vez de
      // mostrar "Failed to fetch". A `carregarDados` ja foi instruida a
      // NAO chamar setErro nem zerar `dados` quando isNetworkError ===
      // true, para evitar o flash de erro na UI.
      if (isNetworkError(error)) {
        try {
          const restaurada = await restaurarPlanilhaLocal({ silencioso: true });
          if (restaurada) {
            setPlanilhaErro(
              "Servidor da planilha indisponível. Usando copia local salva no navegador."
            );
            setErro(null);
            return null;
          }
        } catch {
          /* deixa o erro original propagar */
        }
        // Se nao houver copia local, agora SIM mostramos o erro porque
        // realmente nao temos o que renderizar.
        setErro("Servidor offline e sem copia local da planilha. Selecione o arquivo novamente.");
      }
      throw error;
    }
  }

  async function selecionarPlanilha() {
    setAlterandoPlanilha(true);
    setPlanilhaErro(null);

    try {
      if (!HAS_PLANILHA_API) {
        return { cancelado: true, origem: "navegador" };
      }

      const resultado = await abrirSeletorPlanilha();

      if (resultado?.cancelado) {
        if (resultado?.caminhoAtual || resultado?.arquivoEncontrado || resultado?.status) {
          setStatusPlanilha(resultado);
        }
        return resultado;
      }

      setStatusPlanilha(resultado);
      setPlanilhaErro(resultado?.erroConfiguracao || null);
      await carregarDados();
      return resultado;
    } catch (error) {
      if (isNetworkError(error)) {
        return { cancelado: true, origem: "navegador" };
      }

      setPlanilhaErro(error.message);
      throw error;
    } finally {
      setAlterandoPlanilha(false);
    }
  }

  async function selecionarPlanilhaPersistente() {
    if (HAS_PLANILHA_API || !supportsPersistentFilePicker()) {
      return { cancelado: true, origem: "navegador", naoSuportado: true };
    }

    setAlterandoPlanilha(true);
    setPlanilhaErro(null);

    try {
      const [handle] = await window.showOpenFilePicker({
        multiple: false,
        types: [
          {
            description: "Planilhas Excel",
            accept: {
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
              "application/vnd.ms-excel.sheet.macroEnabled.12": [".xlsm"],
            },
          },
        ],
      });

      if (!handle) {
        return { cancelado: true, origem: "navegador" };
      }

      const allowed = await requestFileHandlePermission(handle);
      if (!allowed) {
        throw new Error("Permissão negada para manter acesso a planilha selecionada.");
      }

      const file = await handle.getFile();
      let acessoPersistente = true;

      try {
        await writeStoredFileHandle(handle);
      } catch (error) {
        acessoPersistente = false;
        setPlanilhaErro(
          "Planilha carregada, mas o navegador não permitiu salvar o acesso persistente ao arquivo."
        );
      }

      const status = await carregarArquivoLocal(file, { persist: true, handle });
      return { ...status, acessoPersistente };
    } catch (error) {
      if (error?.name === "AbortError") {
        return { cancelado: true, origem: "navegador" };
      }

      setPlanilhaErro(error.message);
      throw error;
    } finally {
      setAlterandoPlanilha(false);
    }
  }

  async function uploadPlanilha(file) {
    setAlterandoPlanilha(true);
    setPlanilhaErro(null);

    try {
      setArquivoPlanilha(file || null);
      setArquivoSignature(getFileSignature(file));

      if (HAS_PLANILHA_API) {
        try {
          const dataUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = reject;
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(file);
          });

          const base64 = dataUrl.split(',')[1];

          const resultado = await fetchJson('/api/planilha/upload', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ filename: file.name, contentBase64: base64 }),
          });

          setStatusPlanilha(resultado);
          setPlanilhaErro(resultado?.erroConfiguracao || null);
          setArquivoHandle(null);
          await clearStoredFileHandle();
          await carregarDados();
          return resultado;
        } catch (error) {
          if (!isNetworkError(error)) {
            throw error;
          }
        }
      }

      return carregarArquivoLocal(file, { persist: true });
    } catch (error) {
      setPlanilhaErro(error.message);
      throw error;
    } finally {
      setAlterandoPlanilha(false);
    }
  }

  function removerPlanilha() {
    clearStoredPlanilha();
    clearStoredFileHandle();
    setArquivoPlanilha(null);
    setArquivoHandle(null);
    setArquivoSignature("");
    setDados([]);
    setMeta(null);
    setExtratoCapacidade(null);
    setVersaoArquivo(null);
    setErro(null);
    setPlanilhaErro(null);
    setStatusPlanilha(statusSemPlanilha());
    md5ServidorRef.current = null;
    setFonte(FONTE_ARQUIVO);
    setAtualizadoEm(null);
  }

  // Tenta restaurar a planilha salva localmente (handle persistente ou
  // blob em IndexedDB). Centralizado para reuso entre o boot inicial e
  // o fallback quando o servidor Express local nao responde.
  async function restaurarPlanilhaLocal({ silencioso = false } = {}) {
    let storedHandle = null;
    try {
      storedHandle = await readStoredFileHandle();
    } catch {
      storedHandle = null;
    }
    const handleFile = await getFileFromHandle(storedHandle);
    if (handleFile) {
      await carregarArquivoLocal(handleFile, { persist: true, handle: storedHandle });
      return true;
    }
    const stored = readStoredPlanilha();
    const storedFile = await storedPlanilhaToFile(stored);
    if (storedFile) {
      await carregarArquivoLocal(storedFile, { persist: false });
      return true;
    }
    if (!silencioso) {
      setStatusPlanilha(statusSemPlanilha());
    }
    return false;
  }

  // ---------- DISTRIBUICAO DE PROJETOS ----------

  async function carregarArquivoDistribuicao(file, { persist = true } = {}) {
    if (!file) {
      throw new Error("Nenhum arquivo de Distribuicao foi selecionado.");
    }

    setPlanilhaDistErro(null);
    setArquivoDist(file);

    if (persist) {
      try {
        await writeStoredDistPlanilha(file);
      } catch (error) {
        setPlanilhaDistErro(
          "Planilha de Distribuição carregada, mas não foi possível salvá-la no armazenamento local."
        );
      }
    }

    toast.info(`Carregando planilha de Distribuição ${file.name}…`, {
      dedupKey: "planilha-dist-carregando",
    });
    let resultado;
    try {
      resultado = await parsearPlanilhaSemTravar("distribuicao", file);
    } catch (error) {
      toast.error(
        `Falha ao carregar a planilha de Distribuição: ${error.message}`,
        { dedupKey: "planilha-dist-erro" },
      );
      throw error;
    }
    setDadosDistribuicao(resultado.ambientes || []);
    setMetaDistribuicao(resultado.meta || null);
    setStatusPlanilhaDist({
      status: "ok",
      caminhoAtual: resultado?.meta?.caminhoArquivo || null,
      arquivoEncontrado: resultado?.meta?.arquivoLido || null,
      timestamp: resultado?.meta?.timestamp || new Date().toISOString(),
    });
    toast.success(
      `Planilha de Distribuição carregada: ${resultado.ambientes?.length || 0} ambientes.`,
      { dedupKey: "planilha-dist-ok" },
    );
    return resultado;
  }

  async function uploadPlanilhaDist(file) {
    setAlterandoPlanilhaDist(true);
    setPlanilhaDistErro(null);
    try {
      return await carregarArquivoDistribuicao(file, { persist: true });
    } catch (error) {
      setPlanilhaDistErro(error.message);
      throw error;
    } finally {
      setAlterandoPlanilhaDist(false);
    }
  }

  async function recarregarPlanilhaDist() {
    setPlanilhaDistErro(null);
    if (arquivoDist) {
      return uploadPlanilhaDist(arquivoDist);
    }
    const stored = readStoredDistMeta();
    const storedFile = await storedDistToFile(stored);
    if (storedFile) {
      return uploadPlanilhaDist(storedFile);
    }
    const message = "Selecione a planilha de Distribuicao para carregar os dados.";
    setPlanilhaDistErro(message);
    throw new Error(message);
  }

  function removerPlanilhaDist() {
    clearStoredDistMeta();
    setArquivoDist(null);
    setDadosDistribuicao([]);
    setMetaDistribuicao(null);
    setPlanilhaDistErro(null);
    setStatusPlanilhaDist(null);
  }

  async function restaurarPlanilhaDistLocal() {
    const stored = readStoredDistMeta();
    const storedFile = await storedDistToFile(stored);
    if (storedFile) {
      try {
        await carregarArquivoDistribuicao(storedFile, { persist: false });
        return true;
      } catch (error) {
        setPlanilhaDistErro(
          "Não foi possível restaurar a planilha de Distribuição salva. Selecione o arquivo novamente."
        );
        return false;
      }
    }
    return false;
  }

  // -----------------------------------------------

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        await restaurarPlanilhaDistLocal();
      } catch {
        /* erro ja tratado em restaurarPlanilhaDistLocal */
      }
      if (!ativo) return;
    })();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let ativo = true;

    (async () => {
      // Servidor configurado para a unidade: e a fonte, em qualquer aparelho.
      // O fluxo abaixo (arquivo / copia local / Express) vira o plano B.
      if (await sincronizarComServidor()) {
        if (ativo) setLoading(false);
        return;
      }
      if (!ativo) return;

      if (!HAS_PLANILHA_API) {
        try {
          const restaurada = await restaurarPlanilhaLocal();
          if (!ativo) return;
          if (!restaurada) {
            setStatusPlanilha(statusSemPlanilha());
          }
        } catch (error) {
          if (ativo) {
            setStatusPlanilha(statusSemPlanilha());
            setPlanilhaErro("Não foi possível restaurar a planilha salva. Selecione o arquivo novamente.");
          }
        } finally {
          if (ativo) {
            setLoading(false);
          }
        }
        return;
      }

      try {
        const status = await buscarStatusPlanilha();
        if (!ativo) return;
        setStatusPlanilha(status);
        setPlanilhaErro(status?.erroConfiguracao || null);
      } catch (error) {
        // Servidor offline ou retornou HTML em vez de JSON.
        // Tentaremos fallback ao IndexedDB abaixo.
        if (!isNetworkError(error)) {
          // erro nao-de-rede: registra mas continua para o fetch principal
          setPlanilhaErro(error.message || null);
        }
      }

      try {
        const json = await buscarDadosPlanilha();
        if (!ativo) return;

        setDados(json.gerencial || []);
        setMeta(json.meta || null);
        setExtratoCapacidade(json.capacidade || null);
        setVersaoArquivo(null);
        setErro(null);
        setPlanilhaErro(null);
        setStatusPlanilha((atual) => ({
          ...(atual || {}),
          status: "ok",
          origem: json?.meta?.origemArquivo || atual?.origem || null,
          caminhoAtual: json?.meta?.caminhoArquivo || atual?.caminhoAtual || null,
          arquivoEncontrado: json?.meta?.arquivoLido || atual?.arquivoEncontrado || null,
          timestamp: json?.meta?.timestamp || atual?.timestamp || null,
        }));
      } catch (error) {
        if (!ativo) return;

        // Se o servidor local nao respondeu, tenta a copia local
        // (IndexedDB / FileSystem handle) — assim o usuario nao
        // perde a planilha so porque o Express nao subiu.
        if (isNetworkError(error)) {
          try {
            const restaurada = await restaurarPlanilhaLocal({ silencioso: true });
            if (!ativo) return;
            if (restaurada) {
              setPlanilhaErro(
                "Servidor da planilha indisponível. Usando copia local salva no navegador."
              );
              return;
            }
          } catch {
            /* ignora — vamos cair no setErro abaixo */
          }
        }

        setDados([]);
        setMeta(null);
        setExtratoCapacidade(null);
        setVersaoArquivo(null);
        setErro(error.message);

        if (error.payload?.statusPlanilha) {
          setStatusPlanilha(error.payload.statusPlanilha);
          setPlanilhaErro(error.payload.statusPlanilha?.erroConfiguracao || null);
        }
      } finally {
        if (ativo) {
          setLoading(false);
        }
      }
    })();

    return () => {
      ativo = false;
    };
    // restaurarPlanilhaLocal e estavel (so usa setState/refs); rodar uma
    // vez no mount e o desejado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (HAS_PLANILHA_API || !arquivoHandle) {
      return undefined;
    }

    let cancelado = false;
    let atualizando = false;

    const verificarAtualizacao = async () => {
      if (atualizando) return;
      atualizando = true;

      try {
        const file = await getFileFromHandle(arquivoHandle);
        if (!file || cancelado) return;

        const nextSignature = getFileSignature(file);
        if (arquivoSignature && nextSignature !== arquivoSignature) {
          setAlterandoPlanilha(true);
          await carregarArquivoLocal(file, { persist: true, handle: arquivoHandle });
        }
      } catch (error) {
        if (!cancelado) {
          setPlanilhaErro("Não foi possível atualizar automaticamente a planilha salva.");
        }
      } finally {
        if (!cancelado) {
          setAlterandoPlanilha(false);
        }
        atualizando = false;
      }
    };

    const intervalId = window.setInterval(verificarAtualizacao, AUTO_RELOAD_INTERVAL_MS);

    return () => {
      cancelado = true;
      window.clearInterval(intervalId);
    };
    // `carregarArquivoLocal` fica fora das dependencias de proposito: e
    // recriada a cada render e inclui-la rearmaria o intervalo de
    // auto-atualizacao continuamente. O efeito reage a TROCA de arquivo
    // (handle/assinatura); a chamada usa a versao corrente via closure.
    // (Aviso pre-existente que o cache do eslint escondia — aflorou quando a
    // edicao de 2026-08-04 invalidou o cache deste arquivo.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arquivoHandle, arquivoSignature]);

  // Fonte servidor: confere o status a cada 30 s e so baixa quando o md5
  // muda. Com a aba oculta nao pergunta nada; ao voltar, confere na hora.
  useEffect(() => {
    if (!servidorConfigurado) return undefined;

    const verificar = () => {
      if (paginaOculta()) return;
      fnsRef.current.sincronizarComServidor();
    };
    const aoMudarVisibilidade = () => {
      if (!paginaOculta()) verificar();
    };

    const intervalId = window.setInterval(verificar, VERIFICACAO_SERVIDOR_MS);
    document.addEventListener("visibilitychange", aoMudarVisibilidade);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", aoMudarVisibilidade);
    };
  }, [servidorConfigurado]);

  // initialLoading = true somente no primeiro carregamento (sem dados em
  // cache). Páginas usam essa flag para mostrar a animação de marcenaria;
  // refresh em background (loading=true mas dados.length > 0) deve passar
  // silencioso para não piscar a tela.
  const initialLoading = loading && dados.length === 0;

  // O value do Provider e memoizado: sem isso, qualquer render deste
  // componente (navegacao — o shell le useLocation — ou qualquer toast, ja
  // que este Provider consome useToast e o value do ToastContext muda a
  // cada add/remove) produzia objeto novo e re-renderizava TODOS os
  // consumidores de useDados(), inclusive telas legado com 400+ linhas
  // montadas. As funcoes sao expostas via wrappers estaveis que leem a
  // versao corrente por ref (mesmo padrao do ToastContext) — nao entram
  // nas deps e nunca ficam stale.
  async function recarregarOrdemExecutivo() {
    setRevisaoAntesDesde(await lerOrdemExecutivo(requestRef.current, planilhaContratada));
  }

  useEffect(() => {
    if (!autenticado || !planilhaContratada) {
      setRevisaoAntesDesde(null);
      return undefined;
    }
    let vivo = true;
    lerOrdemExecutivo(requestRef.current, planilhaContratada).then((data) => {
      if (vivo) setRevisaoAntesDesde(data);
    });
    return () => {
      vivo = false;
    };
  }, [autenticado, planilhaContratada]);

  const dadosComOrdem = useMemo(
    () => carimbarOrdem(dados, revisaoAntesDesde),
    [dados, revisaoAntesDesde],
  );

  const fnsRef = useRef({});
  fnsRef.current = {
    carregarDados,
    recarregarDados,
    removerPlanilha,
    selecionarPlanilha,
    selecionarPlanilhaPersistente,
    uploadPlanilha,
    uploadPlanilhaDist,
    recarregarPlanilhaDist,
    removerPlanilhaDist,
    sincronizarComServidor,
    recarregarOrdemExecutivo,
  };
  const apiEstavel = useMemo(() => {
    const wrap =
      (nome) =>
      (...args) =>
        fnsRef.current[nome](...args);
    return {
      carregarDados: wrap("carregarDados"),
      recarregarDados: wrap("recarregarDados"),
      removerPlanilha: wrap("removerPlanilha"),
      selecionarPlanilha: wrap("selecionarPlanilha"),
      selecionarPlanilhaPersistente: wrap("selecionarPlanilhaPersistente"),
      uploadPlanilha: wrap("uploadPlanilha"),
      uploadPlanilhaDist: wrap("uploadPlanilhaDist"),
      recarregarPlanilhaDist: wrap("recarregarPlanilhaDist"),
      removerPlanilhaDist: wrap("removerPlanilhaDist"),
      sincronizarComServidor: wrap("sincronizarComServidor"),
      recarregarOrdemExecutivo: wrap("recarregarOrdemExecutivo"),
    };
  }, []);

  const value = useMemo(
    () => ({
      // Linhas do GERENCIAL com a ordem do executivo carimbada.
      dados: dadosComOrdem,
      revisaoAntesDesde,
      meta,
      loading,
      initialLoading,
      erro,
      statusPlanilha,
      planilhaErro,
      alterandoPlanilha,
      // Fonte "servidor" (Google Drive via backend) x "arquivo" do aparelho.
      fonte,
      atualizadoEm,
      erroServidor,
      servidorConfigurado,
      // Capacidade da Fabrica (extrato por cabecalho + versao do Drive).
      extratoCapacidade,
      versaoArquivo,
      // J-086: expor flag para a UI sinalizar "modo navegador" ao usuario.
      modoNavegador: !HAS_PLANILHA_API,
      suportaSelecaoPersistente:
        !HAS_PLANILHA_API && supportsPersistentFilePicker(),
      ...apiEstavel,
      // ---- Segunda fonte: DISTRIBUICAO DE PROJETOS ----
      dadosDistribuicao,
      metaDistribuicao,
      statusPlanilhaDist,
      planilhaDistErro,
      alterandoPlanilhaDist,
    }),
    [
      dadosComOrdem,
      revisaoAntesDesde,
      meta,
      loading,
      initialLoading,
      erro,
      statusPlanilha,
      planilhaErro,
      alterandoPlanilha,
      fonte,
      atualizadoEm,
      erroServidor,
      servidorConfigurado,
      extratoCapacidade,
      versaoArquivo,
      apiEstavel,
      dadosDistribuicao,
      metaDistribuicao,
      statusPlanilhaDist,
      planilhaDistErro,
      alterandoPlanilhaDist,
    ],
  );

  return <DadosContext.Provider value={value}>{children}</DadosContext.Provider>;
}

export const useDados = () => useContext(DadosContext);

// Hook derivado: ambientes da DISTRIBUICAO ja unidos aos dados cadastrais
// do GERENCIADOR pela chave NUM. PROJETO (string).
//
// Retorna { ambientes, gerencialDisponivel }:
//  - ambientes: lista enriquecida com campos do GERENCIAL onde havia match.
//    Quando o Gerenciador nao esta carregado, campos cadastrais vem null
//    e `gerencialDisponivel` e false (a UI mostra banner de aviso).
//  - O matching e estrito como string trimada (preserva "439.13" sem perder
//    sufixo decimal). Quando uma linha do Gerenciador nao tem NUM. PROJETO,
//    ela e ignorada no join.
export function useAmbientesComJoin() {
  const ctx = useContext(DadosContext) || {};
  // Estabilizamos as referencias via useMemo: `ctx.dadosDistribuicao || []`
  // criaria array novo a cada render se o ctx ainda nao tem o campo, e
  // dispararia recomputacao infinita nos useMemo dependentes.
  const ambientesDist = useMemo(
    () => ctx.dadosDistribuicao || [],
    [ctx.dadosDistribuicao]
  );
  const dadosGerencial = useMemo(() => ctx.dados || [], [ctx.dados]);
  const gerencialDisponivel = dadosGerencial.length > 0;

  const indexGerencial = useMemo(() => {
    const mapa = new Map();
    for (const linha of dadosGerencial) {
      const chave = linha?.[F.NUMERO_DO_PROJETO];
      if (chave === null || chave === undefined || chave === "") continue;
      const chaveTexto = String(chave).trim();
      if (!chaveTexto) continue;
      // Preserva a 1a ocorrencia — duplicatas no Gerenciador sao raras mas
      // possiveis; usuario decide qual e canonica via tela do PCP.
      if (!mapa.has(chaveTexto)) {
        mapa.set(chaveTexto, linha);
      }
    }
    return mapa;
  }, [dadosGerencial]);

  const ambientes = useMemo(() => {
    return ambientesDist.map((amb) => {
      const chave = amb?.[D.NUMERO_PROJETO];
      const cadastral = chave ? indexGerencial.get(String(chave).trim()) : null;
      return {
        ...amb,
        cadastral: cadastral || null,
        cliente: cadastral?.[F.NOME_DO_CLIENTE] || null,
        ambiente: cadastral?.[F.AMBIENTE] || null,
        vendedor: cadastral?.[F.VENDEDOR] || null,
        consultor: cadastral?.[F.CONSULTOR] || null,
        numeroPedido: cadastral?.[F.NUMERO_DO_PEDIDO] || null,
        dataVenda: cadastral?.[F.DATA_DA_VENDA] || null,
        dataMedicao: cadastral?.[F.MEDICAO_REALIZADO] || null,
        prazoLimiteExecutivo: cadastral?.[F.EXECUTIVO_LIMITE] || null,
        dataAprovacaoGerencial: cadastral?.[F.APROVACAO_DO_EXECUTIVO] || null,
        entregaPrevista: cadastral?.[F.ENTREGA_PREVISTA] || null,
        definicaoAcabamentos: cadastral?.[F.DEFINICAO_ACABAMENTOS_REALIZADO] || null,
        valorFabrica: cadastral?.[F.VALOR_DO_AMBIENTE] ?? null,
      };
    });
  }, [ambientesDist, indexGerencial]);

  return { ambientes, gerencialDisponivel };
}
